import { inject } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import {
    catchError, concatMap, debounceTime, filter, first, map, switchMap, take,
} from 'rxjs/operators';

import { ProductPaginationService } from '../../../services/product-pagination.service';
import { ProductViewedService } from '../../../services/product-viewed.service';
import { mapResponseProductsTransformer } from '../../application/map-response-products.transformer';
import { relatedProductsTransformers } from '../../application/transformers/related-product.transformers';
import { getExcludedProductsUseCase } from '../../application/use-cases/get-excluded-products.use-case';
import { getProductsBatchByIdsUseCase } from '../../application/use-cases/get-product-batch-by-ids.use-case';
import { getProductIdsChunkUseCase } from '../../application/use-cases/get-product-ids-chunk.use-case';
import { getRelatedsCarouselsToShowUseCase } from '../../application/use-cases/get-related-carousels-to-show.use-case';
import { getRelatedProductIdsUseCase } from '../../application/use-cases/get-related-product-ids.use-case';
import { getRelatedsSectionUseCase } from '../../application/use-cases/get-related-section.use-case';
import { mergeCarouselsFromPlaceWithProductsIdsUseCase } from '../../application/use-cases/merge-carousels-from-place-with-products-ids.use-case';
import { RelatedsPageProductsLoad } from '../../domain/models/related-products.models';
import { RELATED_PRODUCTS_REPOSITORY } from '../../ui/tokens/related-products.tokens';

import {
    LoadRelatedCarouselProducts,
    LoadRelatedCarouselProductsError,
    LoadRelatedCarouselProductsSuccess,
    ProductViewed,
    SetRelatedProductViewed,
    SetRelatedsCarouselsForPlace,
    SetRelatedsCarouselsForPlaceError,
    SetRelatedsCarouselsForPlaceSuccess,
    SetRelatedsCarouselsProductsIdsError,
    SetRelatedsCarouselsProductsIdsSuccess,
} from './related-products.actions';
import {
    getRelatedProductsPlaceCarousel,
    getRelatedProductsPlaceCarouselIsLoadingProducts,
} from './related-products.selectors';

import { getCookie } from '@core/application/use-cases/cookies/get-cookie.use-case';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { getPage, getContext } from '@core/infrastructure/state/core.selectors';
import { ProductPublicModels, ProductsService } from '@features/products/product';
import { ProductsArrayPublicPort } from '@features/products/productsArray/infrastructure/public/public.port';
import { WishlistQueriesFacade } from '@features/wishlist/infrastructure/facades/queries/wishlist.queries.facades';
import { ItxObservabilityCommands } from '@frontend/libs/itx-observability-core';
import { getProductPageDetail } from '@pages/product';
import { ABTestingService } from '@shared/ab-testing/services/AB-testing.service';
import { getLastSectionSelector } from '@shared/menu-categories/store/menu-categories.selectors';
import { StaticResourceService } from '@shared/services/static-resources/static-resource.service';
import { LoadShopCartSuccess } from '@shared/shopcart/store/shopcart.actions';
import { getPendingGetCart } from '@shared/shopcart/store/shopcart.selectors';
import { getShopCartCore, getShopCartCoreItems } from '@shared/shopcart-core/infrastructure/state/shopcart-core.selectors';

export const SetRelatedsCarouselsForPlace$ = createEffect((
    actions$ = inject(Actions),
    abTestingService = inject(ABTestingService),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(SetRelatedsCarouselsForPlace),
        // Give it a little time so it doesn't react too quickly to rapid changes by the user or rather by QA 😉
        debounceTime(500),
        concatLatestFrom(({ placeName }) => {
            return getRelatedsCarouselsToShowUseCase(
                abTestingService,
                store,
                placeName
            );
        }),
        concatLatestFrom(() => store.select(getShopCartCoreItems)),
        map(([[{ placeName, baseProducts, section }, relatedsCarousels], cartItems]) => {
            try {
                const excludedProducts = getExcludedProductsUseCase(
                    placeName,
                    cartItems,
                    baseProducts as unknown as ProductPublicModels.PublicProduct[],
                );
                const carousels = relatedProductsTransformers.relatedPlaceToRelatedProductsCarousel(
                    relatedsCarousels
                );

                return SetRelatedsCarouselsForPlaceSuccess({
                    placeName,
                    relatedPlace: carousels,
                    excludedProducts,
                    baseProducts,
                    section,
                });
            } catch (error) {
                ItxObservabilityCommands.sendObservabilityLog(error, 'SetRelatedsCarouselsForPlace$');

                return SetRelatedsCarouselsForPlaceError();
            }
        }),
    );
}, { functional: true });

// TO REFACTOR: This effect is too complex and should be split into smaller pieces
export const LoadCarousels$ = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    staticResourceService = inject(StaticResourceService),
    productViewedService = inject(ProductViewedService),
    relatedProductsRepository = inject(RELATED_PRODUCTS_REPOSITORY),
    wishlistQueriesFacade = inject(WishlistQueriesFacade),
    window = inject(WINDOW) as unknown as CommonWindow,
) => {
    return actions$.pipe(
        ofType(SetRelatedsCarouselsForPlaceSuccess),
        concatMap((action) => {
            if (action.placeName !== 'CART') {
                return of(action);
            }

            const pendingItems = store.selectSignal(getPendingGetCart)();

            if (!pendingItems?.length) {
                return of(action);
            }

            return actions$.pipe(ofType(LoadShopCartSuccess), take(1), map(() => action));
        }),
        concatMap(({
            placeName, relatedPlace, excludedProducts, baseProducts, section,
        }) => {
            const relatedRequests = Object.values(relatedPlace).map((carousel) => {
                // PRE: el amount sirve para definir el número exacto de productos que queremos.
                // Si le pasamos -1 nos devolverá todos los dels itemsConfig.
                return combineLatest([
                    store.select(getProductPageDetail),
                    store.select(getLastSectionSelector),
                    store.select(getPage),
                    store.select(getShopCartCore),
                ]).pipe(
                    take(1),
                    switchMap(([productPageDetail, lastSection, page, shopCart]) => {
                        const relatedsSection = getRelatedsSectionUseCase(
                            page,
                            productPageDetail?.section,
                            section || lastSection,
                            shopCart,
                            placeName,
                        );
                        const sessionId = getCookie(window.document.cookie, 'ITXSESSIONID');
                        const itemsConfig = carousel.relatedTypes;

                        return getRelatedProductIdsUseCase(
                            staticResourceService,
                            store,
                            productViewedService,
                            relatedProductsRepository,
                            wishlistQueriesFacade,
                            itemsConfig,
                            relatedsSection,
                            0,
                            baseProducts,
                            sessionId,
                            placeName,
                        );
                    })
                );
            });

            return combineLatest(
                relatedRequests
            ).pipe(
                first(),
                catchError((error) => {
                    return of(error);
                }),
                map((productsIds) => {
                    // Este control de errores es necesario para evitar que se rompan los tests
                    if (!Array.isArray(productsIds) || productsIds.some((ids) => ids === null)) {
                        const error = productsIds;
                        ItxObservabilityCommands.sendObservabilityLog(error, 'LoadCarousels$');
                        return SetRelatedsCarouselsProductsIdsError({ placeName });
                    }

                    const carousels = mergeCarouselsFromPlaceWithProductsIdsUseCase(
                        relatedPlace,
                        productsIds,
                        excludedProducts
                    );
                    return SetRelatedsCarouselsProductsIdsSuccess({ placeName, carousels, excludedProducts });
                })
            );
        }),
    );
}, { functional: true });

export const LoadFirstProductsCarousels$ = createEffect((
    actions$ = inject(Actions),
) => {
    return actions$.pipe(
        ofType(SetRelatedsCarouselsProductsIdsSuccess),
        concatMap(({ placeName, carousels }) => {
            return Object.values(carousels)?.map((carousel) => {
                return LoadRelatedCarouselProducts({
                    placeName, carousel, page: 0,
                });
            });
        }),
    );
}, { functional: true });

export const LoadRelatedCarouselProducts$ = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    productsArrayPublicPort = inject(ProductsArrayPublicPort),
    productsService = inject(ProductsService),
    paginationService = inject(ProductPaginationService),
) => {
    return actions$.pipe(
        ofType(LoadRelatedCarouselProducts),
        concatLatestFrom(({ placeName, carousel }) => combineLatest([
            store.select(getContext),
            store.select(getRelatedProductsPlaceCarousel(placeName, carousel.id)),
        ])),
        concatMap(([{ placeName, page }, [context, carousel]]) => {
            try {
                const itemsIdsGroups = getProductIdsChunkUseCase(
                    carousel.itemsIds,
                    // Fallback para retrocompatibilidad con la configuración actual de PRO
                    RelatedsPageProductsLoad[carousel?.view] || RelatedsPageProductsLoad.carrusel,
                    page
                );

                // Una request por cada tipo de realacionados ya que algunos requieren peticiones a productArray y otros a mocaArray
                const productsRequests = itemsIdsGroups.map((itemsIds) => {
                    return getProductsBatchByIdsUseCase(
                        productsArrayPublicPort,
                        store,
                        productsService,
                        paginationService,
                        context,
                        itemsIds,
                        itemsIds[0]?.relatedType,
                    );
                });
                if (productsRequests?.length) {
                    return combineLatest(productsRequests).pipe(map((requestProducts) => {
                        const { productsMap, filteredItemsIds } = mapResponseProductsTransformer(
                            requestProducts,
                            carousel,
                            itemsIdsGroups,
                        );
                        return LoadRelatedCarouselProductsSuccess({
                            placeName, carousel, page, productsMap, filteredItemsIds,
                        });
                    }));
                }
                return of(LoadRelatedCarouselProductsSuccess({
                    placeName, carousel, page, productsMap: {},
                }));
            } catch (error) {
                ItxObservabilityCommands.sendObservabilityLog(error, 'LoadRelatedCarouselProducts$');
                return of(LoadRelatedCarouselProductsError({ placeName, carousel, page }));
            }
        }),
    );
}, { functional: true });

export const TrackViewedProductsForLazyLoading$ = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(ProductViewed),
        filter(({ location }) => !!location),
        concatLatestFrom(({ placeName, location }) => store.select(getRelatedProductsPlaceCarousel(placeName, location))),
        filter(([_, carousel]) => !!carousel),
        map(([{ placeName }, carousel]) => SetRelatedProductViewed({ placeName: placeName, carousel }))
    );
}, { functional: true });

export const LoadNextRelatedCarouselProducts$ = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(SetRelatedProductViewed),
        // Si no se han cargado todos los productos ya
        filter(({ carousel }) => Object.values(carousel.productsLoaded).length !== carousel.filteredItemsIds.length),
        concatLatestFrom(({ placeName, carousel }) =>
            store.select(getRelatedProductsPlaceCarouselIsLoadingProducts(placeName, carousel.id))),
        // Si nos se están cargando productos actualmente
        filter(([_, isCarouselLoadingProducts]) => !isCarouselLoadingProducts),
        concatLatestFrom(([{ placeName, carousel }]) =>
            store.select(getRelatedProductsPlaceCarousel(placeName, carousel.id))),
        // Cargamos los siguientes productos 3 items antes de llegar al final de los actualmente cargados
        filter(([_, carousel]) => Object.values(carousel.productsLoaded).length - carousel.productsViewed <= 3),
        map(([[{ placeName }], carousel]) => {
            return LoadRelatedCarouselProducts({ placeName, carousel, page: carousel.currentPage + 1 });
        })
    );
}, { functional: true });
